import { useState, type CSSProperties, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Info, Loader2, Search, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { loadPref, savePref } from '../constants';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';

// ============================================================
// Kit visuel de la page Mondes
// ============================================================
// Briques communes aux onglets, bâties sur les classes de WorldsPage.module.scss et les jetons du
// panel (--w-* pour le sens : ok, à surveiller, erreur ; dérivés pour le thème sombre sur .root).
// Aucune couleur en dur ici hors couleurs de données (pays, régions, biomes).
// ============================================================

/** Sens d'un badge, d'une tuile ou d'une icône : vert, ambre, rouge, neutre, accent du jeu. */
export type Tone = 'ok' | 'warn' | 'bad' | 'off' | 'info';

/** Concatène des classes en ignorant les valeurs vides. */
export function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Classe du ton (définit --tone, --tone-bg, --tone-border). */
export function toneClass(tone: Tone | undefined): string {
  return tone ? ws[`tone_${tone}`] ?? '' : '';
}

// ---- États vides ----

export interface EmptyStateProps {
  icon: LucideIcon;
  title: ReactNode;
  text?: ReactNode;
  /** Deux actions au plus (boutons primaryBtn / secondaryBtn du panel). */
  actions?: ReactNode;
  /** Version resserrée (dans une table, une carte de la colonne d'outils). */
  compact?: boolean;
  /** Icône animée : état de chargement. */
  spin?: boolean;
  /** Icône en ambre ou en rouge (erreur) au lieu de l'accent du jeu. */
  tone?: 'warn' | 'bad';
}

/**
 * État vide ou de page : icône de 40 px (28 en compact) à 50 % de l'accent, titre, texte, actions.
 * Toujours distinguer « aucune donnée » (icône de l'onglet + « Nouveau … ») et « aucun résultat »
 * (SearchX + « Effacer la recherche »).
 */
export function EmptyState({ icon: Icon, title, text, actions, compact = false, spin = false, tone }: EmptyStateProps) {
  return (
    <div className={cx(ws.empty, compact && ws.emptyCompact, tone && toneClass(tone), tone && ws.emptyToned)}
      role={spin ? 'status' : undefined}>
      <Icon size={compact ? 28 : 40} className={cx(ws.emptyIcon, spin && ws.spinning)} aria-hidden />
      <h3 className={ws.emptyTitle}>{title}</h3>
      {text && <div className={ws.emptyText}>{text}</div>}
      {actions && <div className={ws.emptyActions}>{actions}</div>}
    </div>
  );
}

/** Ligne de table qui porte un EmptyState compact sur toute la largeur. */
export function EmptyRow({ colSpan, ...rest }: { colSpan: number } & EmptyStateProps) {
  return (
    <tr className={ws.emptyRow}>
      <td colSpan={colSpan}><EmptyState compact {...rest} /></td>
    </tr>
  );
}

// ---- Encarts ----

/**
 * Encart d'explication (accent du jeu), d'avertissement (ambre) ou d'erreur (rouge). Avec
 * `storageKey`, il se replie et l'état replié est mémorisé dans le navigateur (true = replié).
 */
export function InfoBox({ icon, tone = 'info', title, children, storageKey }: {
  icon?: LucideIcon;
  tone?: 'info' | 'warn' | 'bad';
  title?: ReactNode;
  children: ReactNode;
  storageKey?: string;
}) {
  const Icon = icon ?? (tone === 'info' ? Info : TriangleAlert);
  const [collapsed, setCollapsed] = useState(() => (storageKey ? loadPref<boolean>(storageKey, false) === true : false));
  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (storageKey) savePref(storageKey, next);
  };
  return (
    <div className={cx(ws.infoBox, tone !== 'info' && ws[`infoBox_${tone}`])} role="note">
      <Icon size={16} className={ws.infoBoxIcon} aria-hidden />
      <div className={ws.infoBoxBody}>
        {(title || storageKey) && (
          <div className={ws.infoBoxHead}>
            {title && <strong className={ws.infoBoxTitle}>{title}</strong>}
            {storageKey && (
              <button type="button" className={ws.infoBoxToggle} aria-expanded={!collapsed} onClick={toggle}>
                {collapsed ? <ChevronRight size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
                {collapsed ? 'Afficher' : 'Masquer'}
              </button>
            )}
          </div>
        )}
        {!collapsed && <div className={ws.infoBoxText}>{children}</div>}
      </div>
    </div>
  );
}

// ---- Couleurs de données ----

/**
 * Pastille de couleur d'un pays, d'une région ou d'un biome. 10 px (et moins) : point rond pour
 * les listes denses et les légendes ; 16 px et plus : carré à coins de 4 px, ombre intérieure
 * (20 dans les tables, 24 dans les titres de modales, 28 dans les grilles de couleurs).
 */
export function ColorChip({ color, size = 10, title }: { color: string; size?: 10 | 16 | 20 | 24 | 28 | 40 | number; title?: string }) {
  return (
    <span className={cx(ws.colorChip, size < 16 && ws.colorChipRound)}
      style={{ width: size, height: size, background: color }}
      title={title} aria-hidden={title ? undefined : true} />
  );
}

/** Badge teinté à la couleur d'une donnée (région...) : fond 13 %, bord 33 %, point de 8 px. */
export function TintBadge({ color, children, title, onClick }: {
  color: string;
  children: ReactNode;
  title?: string;
  onClick?: () => void;
}) {
  const style = { '--c': color } as CSSProperties;
  const inner = (
    <>
      <i className={ws.tintDot} aria-hidden />
      <span className={ws.tintText}>{children}</span>
    </>
  );
  if (onClick) {
    return <button type="button" className={cx(ws.tintBadge, ws.tintBadgeBtn)} style={style} title={title} onClick={onClick}>{inner}</button>;
  }
  return <span className={ws.tintBadge} style={style} title={title}>{inner}</span>;
}

/**
 * Badge de statut (pilule de 11 px, icône de 12 px, ton --w-*). Avec `onClick`, c'est un bouton
 * (ex. bascule Actif d'un pays) ; `busy` remplace l'icône par un spinner et désactive le bouton.
 */
export function StatusBadge({ tone, icon: Icon, children, title, onClick, busy = false, disabled = false }: {
  tone: Tone;
  icon?: LucideIcon;
  children: ReactNode;
  title?: string;
  onClick?: () => void;
  busy?: boolean;
  disabled?: boolean;
}) {
  const content = (
    <>
      {busy ? <Loader2 size={12} className={ws.spinning} aria-hidden /> : Icon ? <Icon size={12} aria-hidden /> : null}
      <span>{children}</span>
    </>
  );
  const cls = cx(ws.statusBadge, toneClass(tone));
  if (onClick) {
    return (
      <button type="button" className={cx(cls, ws.statusBadgeBtn)} title={title} onClick={onClick}
        disabled={disabled || busy} aria-busy={busy || undefined}>
        {content}
      </button>
    );
  }
  return <span className={cls} title={title}>{content}</span>;
}

// ---- Cartes ----

/** En-tête de carte : icône de 14 px à l'accent du jeu, titre de 13 px, actions à droite. */
export function CardTitle({ icon: Icon, children, actions }: { icon: LucideIcon; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className={ws.cardHead}>
      <h3 className={ws.cardTitle}><Icon size={14} aria-hidden /> {children}</h3>
      {actions && <div className={ws.cardActions}>{actions}</div>}
    </div>
  );
}

/** Touche clavier. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className={ws.kbd}>{children}</kbd>;
}

/** Tuile de statistique (accueil) : icône teintée, libellé, valeur, sous-ligne ; cliquable avec onClick. */
export function StatTile({ icon: Icon, label, value, sub, tone, onClick, title }: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
  title?: string;
}) {
  const body = (
    <>
      <span className={ws.statIcon}><Icon size={20} aria-hidden /></span>
      <span className={ws.statBody}>
        <span className={ws.statLabel}>{label}</span>
        <span className={ws.statValue}>{value}</span>
        {sub && <span className={ws.statSub}>{sub}</span>}
      </span>
    </>
  );
  const cls = cx(ws.statTile, toneClass(tone), onClick && ws.statTileClickable);
  if (onClick) return <button type="button" className={cls} onClick={onClick} title={title}>{body}</button>;
  return <div className={cls} title={title}>{body}</div>;
}

// ---- Boutons ----

/**
 * Action de ligne de 28 px, icône seule : colorée au repos (70 %), pleine au survol. `title` sert
 * d'infobulle et de libellé accessible, y compris désactivé (ex. « Aucun f home à supprimer »).
 */
export function ActionButton({ icon: Icon, title, tone = 'neutral', busy = false, disabled = false, onClick }: {
  icon: LucideIcon;
  title: string;
  tone?: 'neutral' | 'primary' | 'warn' | 'danger';
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const btn = (
    <button type="button" className={cx(ws.actBtn, tone !== 'neutral' && ws[`act_${tone}`])}
      title={title} aria-label={title} disabled={disabled || busy} aria-busy={busy || undefined} onClick={onClick}>
      {busy ? <Loader2 size={14} className={ws.spinning} aria-hidden /> : <Icon size={14} aria-hidden />}
    </button>
  );
  // Un bouton désactivé ne reçoit pas le survol partout : l'enveloppe porte l'infobulle.
  return disabled && !busy ? <span className={ws.actWrap} title={title}>{btn}</span> : btn;
}

/** Bouton d'action de ligne dans une modale (Retirer, Ajouter, Réaffecter) : fond léger au ton. */
export function SoftButton({ tone, busy = false, disabled = false, onClick, children, icon: Icon, title, type = 'button' }: {
  tone: 'primary' | 'success' | 'danger';
  busy?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
  icon?: LucideIcon;
  title?: string;
  type?: 'button' | 'submit';
}) {
  return (
    <button type={type} className={cx(ws.softBtn, ws[`soft_${tone}`])} onClick={onClick} title={title}
      disabled={disabled || busy} aria-busy={busy || undefined}>
      {busy ? <Loader2 size={13} className={ws.spinning} aria-hidden /> : Icon ? <Icon size={13} aria-hidden /> : null}
      {children}
    </button>
  );
}

// ---- Petites briques ----

/** Case vide d'une table : « - » discret. */
export function Dash() {
  return <span className={ws.mutedDash}>-</span>;
}

/** Filtres en puces (un seul actif). `count` masqué quand il est absent. */
export function FilterChips<K extends string>({ items, value, onChange, label = 'Filtres' }: {
  items: { key: K; label: string; count?: number; title?: string }[];
  value: K;
  onChange: (key: K) => void;
  label?: string;
}) {
  return (
    <div className={ws.filterChips} role="group" aria-label={label}>
      {items.map((it) => (
        <button key={it.key} type="button" className={cx(ws.filterChip, it.key === value && ws.filterChipOn)}
          aria-pressed={it.key === value} title={it.title} onClick={() => onChange(it.key)}>
          {it.label}
          {it.count != null && <span className={ws.filterChipCount}>{it.count.toLocaleString('fr-FR')}</span>}
        </button>
      ))}
    </div>
  );
}

/** Champ de recherche du panel (styles.search) avec bouton d'effacement. */
export function SearchField({ value, onChange, placeholder = 'Rechercher', autoFocus = false, className }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  return (
    <div className={cx(styles.search, ws.searchField, className)}>
      <Search size={14} aria-hidden />
      {/* data-esc-clears : Modal laisse passer Échap tant que le champ n'est pas vide. */}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        autoFocus={autoFocus} aria-label={placeholder} data-esc-clears=""
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.stopPropagation(); onChange(''); } }} />
      {value && (
        <button type="button" className={ws.searchClear} onClick={() => onChange('')} title="Effacer la recherche" aria-label="Effacer la recherche">
          <X size={13} aria-hidden />
        </button>
      )}
    </div>
  );
}
